import { VehicleFuelType, VehicleSegment } from '@api/index';
import SimpleErrorState from '@components/SimpleErrorState';
import { Loadable, LoadableType } from '@data/loadable';
import { CalculationParamObject, CalculationParams } from '@features/settings/reducer';
import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import ExpanderList from '@rio-cloud/rio-uikit/ExpanderList';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { TooltipProps } from '@rio-cloud/rio-uikit/Tooltip';
import { Variables } from '@utils/useQuery';
import _ from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Column, RatioColumn } from '../../columns/createColumn';
import usePerformData from '../../data/hooks/usePerformData';
import { attributesForRequest, entitiesForRequest } from '../../data/selectors';
import { State } from '../../setup/types';
import { Attribute, HydratedEntity, PerformSegmentBy, RawDriver } from '../../types';
import { hydrateColumns } from '../../utils/columns';
import {
    acceleration,
    accelerationPerform3,
    accelerationPerform3WithOvertake,
    braking,
    coastingPerform3,
    cruisePerform3,
    cruisePerform3v3,
    cruisePerform3WithOvertake,
    electricAccelerationPedalRating,
    electricCruiseControlRating,
    electricOverspeedRating,
    electricRecuperationRating,
    excessiveIdling,
    fuelCruiseV1,
    harshAcceleration,
    harshBraking,
    overspeed,
} from './configuration';
import Discipline from './Discipline';
import DisciplineTitle from './DisciplineTitle';
import {
    ELECTRIC_RATING_KPIS,
    EXCESSIVE_IDLING_KPIS_ATTRIBUTES,
    FORESIGHTED_COMMON_REQUEST_ATTRIBUTES,
    FORESIGHTED_REQUEST_ATTRIBUTES,
    FORESIGHTED_REQUEST_ATTRIBUTES_OVERTAKE_V1_WITHOUT_CC,
    FORESIGHTED_REQUEST_ATTRIBUTES_OVERTAKE_V2_WITHOUT_CC,
    FORESIGHTED_REQUEST_ATTRIBUTES_OVERTAKE_V3,
    FORESIGHTED_REQUEST_ATTRIBUTES_V1,
    FORESIGHTED_REQUEST_ATTRIBUTES_V2,
    FORESIGHTED_REQUEST_TRUE_DRIVING_RATINGS_KPIS_ATTRIBUTES,
    HARSH_KPIS_ATTRIBUTES,
} from './queries';

interface DisciplineI {
    id: string;
    title?: Column | RatioColumn;
    entries: { title: React.ReactNode; values: (Column | RatioColumn | undefined)[] }[];
}

interface DisciplinesObjI {
    coasting?: DisciplineI;
    accelerationpedal?: DisciplineI;
    braking?: DisciplineI;
    cruisecontrol?: DisciplineI;
    overspeed?: DisciplineI;
    recuperation?: DisciplineI;
}

export const createDisciplineListItem = ({
    calculationParams,
    discipline,
    entity,
    tooltipPlacement,
}: {
    calculationParams: { [key: string]: CalculationParamObject };
    discipline: DisciplineI;
    entity: HydratedEntity;
    tooltipPlacement?: TooltipProps['placement'];
}) => ({
    id: discipline.id,
    header: discipline.title && (
        <DisciplineTitle
            title={discipline.title}
            entity={entity}
            calculationParams={calculationParams}
            tooltipPlacement={tooltipPlacement}
        />
    ),
    body: (
        <div className="text-size-12 text-color-dark">
            {discipline.entries.map(({ title, values }, index) => (
                <Discipline columns={values} title={title} entity={entity} key={index} />
            ))}
        </div>
    ),
});

const failure = () => <SimpleErrorState fullWidth />;
const loading = () => <Spinner />;
const unknown = () => null;

export const ForesightedDriving: React.FC<{
    data: LoadableType<HydratedEntity>;
    calculationParams: CalculationParams;
    children?: ({
        entity,
        attributes,
    }: {
        entity: HydratedEntity;
        attributes: Attribute[];
    }) => React.ReactNode;
    disciplines?: DisciplineI[];
    shouldShowOvertakeInCruise?: boolean;
    shouldUseV2Ratings?: boolean;
    shouldShowHarshKPIs?: boolean;
    shouldShowExcessiveKPIs?: boolean;
    shouldUseV3Ratings?: boolean;
    attributes?: Attribute[];
}> = ({
    data = Loadable.createNotRequested(),
    calculationParams,
    children,
    disciplines,
    shouldShowOvertakeInCruise = false,
    shouldUseV2Ratings = true,
    shouldShowHarshKPIs = false,
    shouldShowExcessiveKPIs = false,
    shouldUseV3Ratings = false,
    attributes = [],
}) => {
    const normalizedCalculationParams = React.useMemo(() => {
        const normalized: { [x: string]: CalculationParamObject } = {};
        Object.keys(calculationParams).forEach((calcParamKey) => {
            normalized[calcParamKey.replace('Rating', '').toLowerCase()] =
                calculationParams[calcParamKey as keyof CalculationParams];
        });
        return normalized;
    }, [calculationParams]);

    const entity = Loadable.cata(
        data,
        (d) => d,
        () => null,
        () => null,
        () => null,
    );

    const containsCityOrInterCityBus =
        entity?.vehicles?.some(
            (vehicle) =>
                vehicle.segment === VehicleSegment.INTER_CITY ||
                vehicle.segment === VehicleSegment.CITY,
        ) ?? false;

    const shouldShowBevKPIs =
        entity?.vehicles?.some((vehicle) => vehicle.fuelType === VehicleFuelType.ELECTRIC) ?? false;

    const isOnlyTruE =
        entity?.vehicles?.every((vehicle) => vehicle.fuelType === VehicleFuelType.ELECTRIC) ??
        false;

    const disciplinesToDisplay = React.useMemo(() => {
        const usesDefaultDisciplines = _.isUndefined(disciplines);
        let finalDisciplines = disciplines;

        if (usesDefaultDisciplines) {
            if (shouldUseV3Ratings) {
                finalDisciplines = isOnlyTruE
                    ? [electricCruiseControlRating, electricOverspeedRating]
                    : [coastingPerform3, acceleration, braking, cruisePerform3v3, overspeed];
            } else if (shouldUseV2Ratings) {
                if (shouldShowOvertakeInCruise) {
                    finalDisciplines = isOnlyTruE
                        ? [electricCruiseControlRating, electricOverspeedRating]
                        : [
                              coastingPerform3,
                              accelerationPerform3WithOvertake,
                              braking,
                              cruisePerform3WithOvertake,
                              overspeed,
                          ];
                } else {
                    finalDisciplines = isOnlyTruE
                        ? [electricCruiseControlRating, electricOverspeedRating]
                        : [
                              coastingPerform3,
                              accelerationPerform3,
                              braking,
                              cruisePerform3,
                              overspeed,
                          ];
                }
            } else {
                finalDisciplines = isOnlyTruE
                    ? [electricCruiseControlRating, electricOverspeedRating]
                    : [coastingPerform3, acceleration, braking, fuelCruiseV1, overspeed];
            }
        }

        if (usesDefaultDisciplines && shouldShowBevKPIs && isOnlyTruE) {
            finalDisciplines = [
                electricCruiseControlRating,
                electricOverspeedRating,
                electricAccelerationPedalRating,
                electricRecuperationRating,
            ];
        }

        const disciplineList = finalDisciplines ? [...finalDisciplines] : [];

        if (usesDefaultDisciplines && shouldShowHarshKPIs && containsCityOrInterCityBus) {
            disciplineList.push(harshAcceleration, harshBraking);
        }

        if (usesDefaultDisciplines && shouldShowExcessiveKPIs && containsCityOrInterCityBus) {
            disciplineList.push(excessiveIdling);
        }

        const hydratedDisciplines = disciplineList.map((discipline) => ({
            ...discipline,
            title: discipline.title
                ? hydrateColumns([discipline.title as Column], attributes, { plain: true })[0]
                : undefined,
            entries: discipline.entries.map((entry) => ({
                ...entry,
                values: hydrateColumns(entry.values.filter(Boolean) as Column[], attributes),
            })),
        }));

        const disciplinesMap: DisciplinesObjI = Object.fromEntries(
            hydratedDisciplines.map((item: DisciplineI) => [item.id.toLowerCase(), item]),
        );

        const hiddenColumns = Object.keys(normalizedCalculationParams).reduce(
            (acc: string[], columnName) => {
                const param = normalizedCalculationParams[columnName];
                const isElectricParam =
                    columnName.startsWith('electric') || columnName === 'recuperation';

                const shouldHide =
                    (!param.enabled && param.weight === 0) ||
                    (isOnlyTruE && !isElectricParam) ||
                    (!isOnlyTruE && isElectricParam);
                const columnToHide =
                    isElectricParam && columnName !== 'recuperation'
                        ? columnName + 'rating'
                        : columnName;
                return shouldHide ? [...acc, columnToHide] : acc;
            },
            [],
        );

        hiddenColumns.forEach((columnName) => {
            const disciplineKey =
                columnName === 'recuperation' ? 'electricrecuperationrating' : columnName;
            delete disciplinesMap[disciplineKey as keyof DisciplinesObjI];
        });

        return disciplinesMap;
    }, [
        attributes,
        containsCityOrInterCityBus,
        disciplines,
        isOnlyTruE,
        normalizedCalculationParams,
        shouldShowBevKPIs,
        shouldShowExcessiveKPIs,
        shouldShowHarshKPIs,
        shouldShowOvertakeInCruise,
        shouldUseV2Ratings,
        shouldUseV3Ratings,
    ]);

    const expanderItems = React.useMemo(() => {
        if (!entity) {
            return [];
        }
        const disciplines = Object.values(disciplinesToDisplay);
        return disciplines.map((discipline: DisciplineI, index) => {
            const isLastDiscipline = index === disciplines.length - 1;
            return createDisciplineListItem({
                calculationParams: normalizedCalculationParams,
                discipline,
                entity,
                tooltipPlacement: isLastDiscipline ? 'top-start' : undefined,
            });
        });
    }, [disciplinesToDisplay, entity, normalizedCalculationParams]);

    const successContent = entity ? (
        <React.Fragment>
            <div>
                {children?.({ entity, attributes })}
                <div className="padding-bottom-10 padding-top-20 text-size-14">
                    <FormattedMessage id="foresightedDriving.signalsTitle" />
                </div>
            </div>
            <ExpanderList items={expanderItems} />
        </React.Fragment>
    ) : (
        <EmptyState headline={<FormattedMessage id="noData" />} fullWidth />
    );

    return Loadable.cata(data, () => successContent, failure, loading, unknown);
};

const isMatchingEntityForDriver = (driverId: string | null) => (entity: HydratedEntity) =>
    (entity?.drivers?.length === 0 && driverId === null) ||
    (entity?.drivers?.length === 1 && driverId === (entity.drivers as RawDriver[])?.[0]?.driverId);

const entitiesAndAttributesSelector = (state: State, requestId: string) => {
    const entities = entitiesForRequest(state, requestId);
    const attributes = attributesForRequest(state, requestId);
    return Loadable.combine((e, a) => ({ entities: e, attributes: a }), entities, attributes);
};

export function ForesightedDrivingByVehicle({
    vehicleIds = [],
    driverIds = [],
    start,
    end,
    children,
    calculationParams,
    shouldShowOvertakeInCruise = false,
    shouldUseV2Ratings,
    shouldShowHarshKPIs = false,
    shouldShowExcessiveIdlingKPIs = false,
    shouldUseV3Ratings = false,
    isElectricVehicle = false,
}: {
    vehicleIds?: string[];
    driverIds?: (string | null)[];
    start: Date;
    end: Date;
    children?: ({
        entity,
        attributes,
    }: {
        entity: HydratedEntity;
        attributes: Attribute[];
    }) => JSX.Element;
    calculationParams: CalculationParams;
    shouldShowOvertakeInCruise?: boolean;
    shouldUseV2Ratings: boolean;
    shouldShowHarshKPIs: boolean;
    shouldShowExcessiveIdlingKPIs: boolean;
    shouldUseV3Ratings?: boolean;
    isElectricVehicle: boolean;
}): JSX.Element {
    const requestDataForOneDriver = driverIds.length === 1;
    const segmentBy = requestDataForOneDriver
        ? PerformSegmentBy.driver_and_vehicle
        : PerformSegmentBy.vehicle;

    // request data for all drivers and select the wanted entry afterward
    let foresightedRequestAttributes: string[] = [];

    if (shouldUseV3Ratings) {
        foresightedRequestAttributes = [
            ...FORESIGHTED_REQUEST_ATTRIBUTES,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_OVERTAKE_V3,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_V2,
        ];
    } else if (shouldUseV2Ratings) {
        foresightedRequestAttributes = [
            ...FORESIGHTED_REQUEST_ATTRIBUTES,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_OVERTAKE_V2_WITHOUT_CC,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_V1,
        ];
    } else {
        foresightedRequestAttributes = [
            ...FORESIGHTED_REQUEST_ATTRIBUTES,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_V1,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_OVERTAKE_V1_WITHOUT_CC,
        ];
    }
    if (isElectricVehicle) {
        foresightedRequestAttributes = [
            ...ELECTRIC_RATING_KPIS,
            ...FORESIGHTED_COMMON_REQUEST_ATTRIBUTES,
        ];
    }

    if (shouldShowHarshKPIs) foresightedRequestAttributes.push(...HARSH_KPIS_ATTRIBUTES);

    if (shouldShowExcessiveIdlingKPIs)
        foresightedRequestAttributes.push(...EXCESSIVE_IDLING_KPIS_ATTRIBUTES);

    if (isElectricVehicle) {
        foresightedRequestAttributes = FORESIGHTED_REQUEST_TRUE_DRIVING_RATINGS_KPIS_ATTRIBUTES;
    }

    const foresightedDrivingData = usePerformData(foresightedRequestAttributes, {
        variables: { start, end, vehicleIds, segmentBy } as Variables,
        debounced: false,
        selector: entitiesAndAttributesSelector,
    }) as LoadableType<{ entities: HydratedEntity[]; attributes: Attribute[] }>;

    const desiredData = React.useMemo(
        () =>
            Loadable.map(foresightedDrivingData, (data) =>
                requestDataForOneDriver
                    ? data.entities.find(isMatchingEntityForDriver(driverIds[0]))
                    : _.head(data.entities),
            ) as LoadableType<HydratedEntity>,
        [foresightedDrivingData, requestDataForOneDriver, driverIds],
    );

    const fetchedAttributes = React.useMemo(
        () =>
            Loadable.cata(
                Loadable.map(foresightedDrivingData, (d) => d.attributes),
                (d) => d,
                () => [],
                () => [],
                () => [],
            ),
        [foresightedDrivingData],
    );

    return (
        <ForesightedDriving
            data={desiredData}
            calculationParams={calculationParams}
            shouldShowOvertakeInCruise={shouldShowOvertakeInCruise}
            shouldUseV2Ratings={shouldUseV2Ratings}
            shouldShowHarshKPIs={shouldShowHarshKPIs}
            shouldShowExcessiveKPIs={shouldShowExcessiveIdlingKPIs}
            shouldUseV3Ratings={shouldUseV3Ratings}
            attributes={fetchedAttributes}
        >
            {children}
        </ForesightedDriving>
    );
}

const isMatchingEntityForVehicle = (vehicleId: string) => (entity: HydratedEntity) =>
    entity?.vehicles?.length === 1 && vehicleId === entity.vehicles[0].vehicleId;

export function ForesightedDrivingByDriver({
    vehicleIds = [],
    driverIds = [],
    start,
    end,
    allowedVehicles,
    children,
    calculationParams,
    shouldShowOvertakeInCruise = false,
    shouldUseV2Ratings,
    shouldShowHarshKPIs = false,
    shouldShowExcessiveIdlingKPIs = false,
    shouldUseV3Ratings = false,
    isOnlyElectric = false,
}: {
    vehicleIds?: string[];
    driverIds: string[];
    allowedVehicles: string[];
    start: Date;
    end: Date;
    children?: ({
        entity,
        attributes,
    }: {
        entity: HydratedEntity;
        attributes: Attribute[];
    }) => JSX.Element;
    calculationParams: CalculationParams;
    shouldShowOvertakeInCruise?: boolean;
    shouldUseV2Ratings: boolean;
    shouldShowHarshKPIs: boolean;
    shouldShowExcessiveIdlingKPIs: boolean;
    shouldUseV3Ratings?: boolean;
    isOnlyElectric: boolean;
}): JSX.Element {
    const requestDataForOneVehicle = vehicleIds.length === 1;
    const segmentBy = requestDataForOneVehicle
        ? PerformSegmentBy.driver_and_vehicle
        : PerformSegmentBy.driver;
    // request data for all perform vehicles and select the wanted entry afterward
    let foresightedRequestAttributes: string[] = [];
    if (shouldUseV3Ratings) {
        foresightedRequestAttributes = [
            ...FORESIGHTED_REQUEST_ATTRIBUTES,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_OVERTAKE_V3,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_V2,
        ];
    } else if (shouldUseV2Ratings) {
        foresightedRequestAttributes = [
            ...FORESIGHTED_REQUEST_ATTRIBUTES,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_OVERTAKE_V2_WITHOUT_CC,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_V1,
        ];
    } else {
        foresightedRequestAttributes = [
            ...FORESIGHTED_REQUEST_ATTRIBUTES,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_V1,
            ...FORESIGHTED_REQUEST_ATTRIBUTES_OVERTAKE_V1_WITHOUT_CC,
        ];
    }

    if (isOnlyElectric) {
        foresightedRequestAttributes = [...ELECTRIC_RATING_KPIS, ...FORESIGHTED_REQUEST_ATTRIBUTES];
    }

    if (shouldShowHarshKPIs) foresightedRequestAttributes.push(...HARSH_KPIS_ATTRIBUTES);

    if (shouldShowExcessiveIdlingKPIs)
        foresightedRequestAttributes.push(...EXCESSIVE_IDLING_KPIS_ATTRIBUTES);

    if (isOnlyElectric) {
        foresightedRequestAttributes = FORESIGHTED_REQUEST_TRUE_DRIVING_RATINGS_KPIS_ATTRIBUTES;
    }

    const foresightedDrivingData = usePerformData(foresightedRequestAttributes, {
        variables: {
            start,
            end,
            driverIds,
            vehicleIds: vehicleIds.filter((v) => allowedVehicles.includes(v)),
            segmentBy,
        } as Variables,
        debounced: false,
        selector: entitiesAndAttributesSelector,
    }) as LoadableType<{ entities: HydratedEntity[]; attributes: Attribute[] }>;

    const desiredData = React.useMemo(
        () =>
            Loadable.map(foresightedDrivingData, (data) =>
                requestDataForOneVehicle
                    ? data.entities.find(isMatchingEntityForVehicle(vehicleIds[0]))
                    : _.head(data.entities),
            ) as LoadableType<HydratedEntity>,
        [foresightedDrivingData, requestDataForOneVehicle, vehicleIds],
    );

    const fetchedAttributes = React.useMemo(
        () =>
            Loadable.cata(
                Loadable.map(foresightedDrivingData, (d) => d.attributes),
                (d) => d,
                () => [],
                () => [],
                () => [],
            ),
        [foresightedDrivingData],
    );

    return (
        <ForesightedDriving
            data={desiredData}
            calculationParams={calculationParams}
            shouldShowOvertakeInCruise={shouldShowOvertakeInCruise}
            shouldUseV2Ratings={shouldUseV2Ratings}
            shouldShowHarshKPIs={shouldShowHarshKPIs}
            shouldShowExcessiveKPIs={shouldShowExcessiveIdlingKPIs}
            shouldUseV3Ratings={shouldUseV3Ratings}
            attributes={fetchedAttributes}
        >
            {children}
        </ForesightedDriving>
    );
}
